/**
 * Capability #10 — duplicate / obsolete / unused test inventory (compose-on-read).
 * Advisory only — never auto-mutates scenarios or flows.
 */
import { z } from 'zod';

export const TEST_INVENTORY_CLASSIFICATIONS = [
  'DUPLICATE',
  'NEAR_DUPLICATE',
  'OBSOLETE',
  'UNUSED',
  'NO_SIGNAL',
] as const;
export type TestInventoryClassification = (typeof TEST_INVENTORY_CLASSIFICATIONS)[number];

export const TEST_INVENTORY_CONFIDENCE_BANDS = ['strong', 'moderate', 'weak'] as const;
export type TestInventoryConfidenceBand = (typeof TEST_INVENTORY_CONFIDENCE_BANDS)[number];

export const TestInventoryGovernanceSchema = z.object({
  autoApply: z.literal(false),
  silentPublish: z.literal(false),
  recommendationsAreNotTruth: z.literal(true),
  advisoryOnly: z.literal(true),
  autoArchive: z.literal(false),
  autoObsolete: z.literal(false),
  autoSkip: z.literal(false),
  claimsTestsSaved: z.literal(false),
});
export type TestInventoryGovernance = z.infer<typeof TestInventoryGovernanceSchema>;

export const TestInventoryFindingSchema = z.object({
  id: z.string().max(80),
  classification: z.enum(TEST_INVENTORY_CLASSIFICATIONS),
  confidenceBand: z.enum(TEST_INVENTORY_CONFIDENCE_BANDS),
  reason: z.string().max(500),
  scenarioId: z.string().uuid(),
  scenarioTitle: z.string().max(300),
  relatedScenarioId: z.string().uuid().nullable().optional(),
  relatedScenarioTitle: z.string().max(300).nullable().optional(),
  evidenceUsed: z.array(z.string().max(200)).max(12).default([]),
  evidenceMissing: z.array(z.string().max(200)).max(8).default([]),
  recommendedAction: z.string().max(400),
  deepLink: z.string().max(500),
});
export type TestInventoryFinding = z.infer<typeof TestInventoryFindingSchema>;

export const TestInventoryIntelligenceResponseSchema = z.object({
  projectId: z.string().uuid(),
  orgId: z.string().uuid(),
  composedAt: z.string(),
  attribution: z.literal('test_inventory_compose'),
  governance: TestInventoryGovernanceSchema,
  summary: z.string().max(600),
  why: z.array(z.string().max(300)).max(8).default([]),
  findings: z.array(TestInventoryFindingSchema).max(50),
  counts: z.object({
    duplicate: z.number().int().nonnegative(),
    nearDuplicate: z.number().int().nonnegative(),
    obsolete: z.number().int().nonnegative(),
    unused: z.number().int().nonnegative(),
    noSignal: z.number().int().nonnegative(),
    scenariosScanned: z.number().int().nonnegative(),
  }),
  limits: z.object({
    maxScenarios: z.number().int().positive(),
    maxFindings: z.number().int().positive(),
    lookbackDays: z.number().int().positive(),
    comparisonStrategy: z.string().max(120),
  }),
});
export type TestInventoryIntelligenceResponse = z.infer<typeof TestInventoryIntelligenceResponseSchema>;
